import React, { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, FileDown, Bookmark } from "lucide-react";
import { api, apiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { hasPerm } from "../../lib/permissions";
import { PageHead, Field, Empty, PortPicker, usePorts, CONTAINERS, CARGOS, money, ago, RateBadge, pdfUrl } from "./common";

const BLANK = { route_name: "", origin_port_id: "", destination_port_id: "", container_type: "20FT", cargo_type: "General", notes: "" };

export default function SavedRoutes() {
  const { user } = useAuth();
  const ports = usePorts();
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const canCreate = hasPerm(user, "sea_freight", "create");
  const canDelete = hasPerm(user, "sea_freight", "delete");

  const load = useCallback(() => {
    api.get("/sea-freight/saved-routes").then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      await api.post("/sea-freight/saved-routes", form);
      toast.success("Route saved");
      setOpen(false); setForm(BLANK); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/sea-freight/saved-routes/${id}`); toast.success("Route removed"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="saved-routes-page">
      <PageHead title="Saved Routes"
        subtitle="Routes you monitor regularly — each card shows the latest recorded rate, the movement against the previous observation and related headlines."
        right={<>
          <Button data-testid="export-routes-pdf" variant="outline" className="rounded-sm gap-1.5" onClick={() => window.open(pdfUrl("/sea-freight/export/saved-routes.pdf"), "_blank")}>
            <FileDown className="w-4 h-4" /> Route report
          </Button>
          {canCreate && <Button data-testid="add-route-btn" className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm gap-1.5" onClick={() => setOpen(true)}><Plus className="w-4 h-4" /> Save route</Button>}
        </>} />

      {rows && rows.length === 0 ? (
        <Empty testid="routes-empty">No saved routes yet — e.g. Shanghai → Mundra, Mundra → Jebel Ali, Mundra → Rotterdam.</Empty>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {(rows || []).map((r) => (
            <section key={r.id} data-testid={`saved-route-${r.id}`} className="bg-white border border-slate-200 rounded-sm">
              <header className="px-4 py-3 border-b border-slate-200 flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-1.5 text-slate-900 font-semibold font-head"><Bookmark className="w-4 h-4 text-slate-400" /> {r.route_name}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{r.origin_port} ({r.origin_unlocode}) → {r.destination_port} ({r.destination_unlocode}) · {r.container_type} · {r.cargo_type}</div>
                </div>
                {canDelete && <Button data-testid={`delete-route-${r.id}`} size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-red-600" onClick={() => remove(r.id)}><Trash2 className="w-4 h-4" /></Button>}
              </header>
              <div className="px-4 py-3">
                {r.latest_rate ? (
                  <div className="flex items-end justify-between gap-3">
                    <div>
                      <div className="text-2xl font-bold font-head text-slate-900">{money(r.latest_rate.total_freight, r.latest_rate.currency)}</div>
                      <div className="mt-1"><RateBadge row={r.latest_rate} /></div>
                      <div className="text-[11px] text-slate-400 mt-1">{r.latest_rate.provider_name} · {r.latest_rate.data_source} · {ago(r.latest_rate.observed_at)}</div>
                    </div>
                    <div className="text-right">
                      {r.change_pct !== null && r.change_pct !== undefined ? (
                        <div className={`text-sm font-semibold ${r.change_pct > 0 ? "text-red-600" : r.change_pct < 0 ? "text-emerald-600" : "text-slate-500"}`} data-testid={`route-change-${r.id}`}>
                          {r.change_pct > 0 ? "+" : ""}{r.change_pct}%
                        </div>
                      ) : <div className="text-xs text-slate-400">no previous rate</div>}
                      <div className="text-[11px] text-slate-400">{r.rate_points} observation(s)</div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-sm px-3 py-2 text-sm" data-testid={`route-no-rate-${r.id}`}>{r.message}</div>
                )}
                {(r.news || []).length > 0 && (
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Related headlines</div>
                    {r.news.slice(0, 3).map((n) => (
                      <a key={n.id} href={n.url} target="_blank" rel="noreferrer" className="block text-xs text-slate-600 hover:text-slate-900 truncate">· {n.headline}</a>
                    ))}
                  </div>
                )}
              </div>
            </section>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="add-route-dialog">
          <DialogHeader><DialogTitle className="font-head">Save a route</DialogTitle>
            <DialogDescription>Rates, trends, news and alerts will be tracked for this lane.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><Field label="Route name (optional)"><Input data-testid="route-name" value={form.route_name} onChange={(e) => setForm({ ...form, route_name: e.target.value })} placeholder="Auto-generated from the ports" className="bg-white h-9" /></Field></div>
            <Field label="Origin port"><PortPicker testid="route-origin" ports={ports} value={form.origin_port_id} onChange={(v) => setForm({ ...form, origin_port_id: v })} /></Field>
            <Field label="Destination port"><PortPicker testid="route-destination" ports={ports} value={form.destination_port_id} onChange={(v) => setForm({ ...form, destination_port_id: v })} /></Field>
            <Field label="Container type">
              <Select value={form.container_type} onValueChange={(v) => setForm({ ...form, container_type: v })}>
                <SelectTrigger data-testid="route-container" className="bg-white h-9"><SelectValue /></SelectTrigger>
                <SelectContent>{CONTAINERS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Cargo type">
              <Select value={form.cargo_type} onValueChange={(v) => setForm({ ...form, cargo_type: v })}>
                <SelectTrigger data-testid="route-cargo" className="bg-white h-9"><SelectValue /></SelectTrigger>
                <SelectContent>{CARGOS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <div className="sm:col-span-2"><Field label="Notes"><Input data-testid="route-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-white h-9" /></Field></div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-route-btn" className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm" onClick={save}>Save route</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
